import React, { useRef, useEffect, useState } from "react";
import { Input, InputRef } from "antd";
import styles from "./OTPInput.module.css";

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
}) => {
  const inputRefs = useRef<(InputRef | null)[]>([]);
  const [otp, setOtp] = useState<string[]>(() => {
    return Array(length)
      .fill("")
      .map((_, i) => value[i] || "");
  });

  useEffect(() => {
    // Sync value prop vào local state
    const newOtp = Array(length)
      .fill("")
      .map((_, i) => value[i] || "");
    setOtp(newOtp);
  }, [value, length]);

  const handleChange = (index: number, inputValue: string) => {
    if (inputValue && !/^\d+$/.test(inputValue)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = inputValue.slice(-1);
    setOtp(newOtp);

    const otpValue = newOtp.join("");
    onChange(otpValue);

    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.input?.focus();
    }

    if (otpValue.length === length && onComplete) {
      onComplete(otpValue);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.input?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = Array(length).fill("");
      pastedData.split("").forEach((char, i) => {
        if (i < length) {
          newOtp[i] = char;
        }
      });
      setOtp(newOtp);
      const otpValue = newOtp.join("");
      onChange(otpValue);

      const lastIndex = Math.min(pastedData.length - 1, length - 1);
      inputRefs.current[lastIndex]?.input?.focus();

      if (otpValue.length === length && onComplete) {
        onComplete(otpValue);
      }
    }
  };

  return (
    <div className={styles.container}>
      {otp.map((digit, index) => (
        <Input
          key={index}
          ref={(ref) => {
            inputRefs.current[index] = ref;
          }}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          maxLength={1}
          autoComplete="off"
          className={`${styles.input} ${digit ? styles.filled : ""}`}
        />
      ))}
    </div>
  );
};
